import CONFIG from "./config";

/**
 * Build a personalized program guide from real client data.
 * Falls back to general educational content when specific data isn't available.
 */
export default function buildGuide({ workouts, mealPlan, stats, program }) {
  const P = CONFIG.program;
  const cycleLength = program?.cycleLength || P.cycleLength;
  const restDays = program?.restDays || P.restDays;
  const restDayStr = restDays.map(d => `Day ${d}`).join(" and ");

  // Count unique workout types from actual program
  const workoutTypes = workouts?.map(w => w.name || w.title).filter(Boolean) || [];
  const uniqueSplits = [...new Set(workoutTypes)];
  const splitDesc = uniqueSplits.length > 0
    ? `Your program runs a ${cycleLength}-day cycle with ${uniqueSplits.length} distinct training sessions: ${uniqueSplits.join(", ")}.`
    : `A ${cycleLength}-day rotating split designed for your goals.`;

  // Real macros from meal plan
  const hasMealPlan = mealPlan && (mealPlan.calories || mealPlan.protein);
  const cals = mealPlan?.calories || "your assigned";
  const protein = mealPlan?.protein || "your assigned";
  const carbs = mealPlan?.carbs || "your assigned";
  const fat = mealPlan?.fat || "your assigned";

  const sections = [
    {
      category: "YOUR PROGRAM",
      items: [
        {
          title: "Your Training Split",
          desc: `${splitDesc} Each muscle group is trained with optimal frequency to maximize growth while allowing adequate recovery between sessions.`,
        },
        {
          title: "Progressive Overload",
          desc: "Progressive overload is the primary driver of long-term adaptation. Forge tracks three overload vectors: weight increase (mechanical tension), volume increase (metabolic stress), and RPE management (effort quality). When you hit the top of the rep range at RPE 7-8, the exercise is flagged for a weight increase.",
        },
        {
          title: "RPE Tracking",
          desc: "Rate of Perceived Exertion (RPE) captures how hard each set actually was for you on that day. A 200lb bench at RPE 6 after great sleep is a different stimulus than 200lb at RPE 9 after poor sleep. RPE data helps detect readiness shifts and time deloads before injury.",
        },
        {
          title: "Rest Day Strategy",
          desc: `${restDayStr} ${restDays.length === 1 ? "is" : "are"} placed to divide your cycle into training blocks, allowing 48-72 hours of recovery per muscle group before it's trained again. Growth happens during recovery.`,
        },
      ],
    },
    {
      category: "NUTRITION",
      items: hasMealPlan ? [
        {
          title: "Your Daily Targets",
          desc: `${cals} kcal daily. ${protein}g protein, ${carbs}g carbs, ${fat}g fat. These targets are calibrated to your goals, body composition, and activity level. Adjust based on weekly weight trends in consultation with your coach.`,
        },
        {
          title: "Meal Timing",
          desc: "Distribute meals across the day to maintain stable energy and optimize protein synthesis. Pre-workout meal 60-90 min before training. Post-workout nutrition within 60 min of your session.",
        },
        {
          title: "Hydration",
          desc: "Minimum 1 gallon daily. Add electrolytes during training if sweating heavily. Track water intake through the Today tab.",
        },
      ] : [
        {
          title: "Nutrition Strategy",
          desc: "Your meal plan targets will appear here once assigned. In the meantime, focus on adequate protein intake (0.8-1g per pound of bodyweight), consistent meal timing, and proper hydration.",
        },
        {
          title: "Hydration",
          desc: "Minimum 1 gallon daily. Track water intake through the Today tab.",
        },
      ],
    },
    {
      category: "RECOVERY",
      items: [
        { title: "Sleep", desc: "7-9 hours minimum. Consistent sleep/wake schedule. Quality sleep is the single most impactful recovery factor." },
        { title: "Rest Days", desc: `${restDayStr} ${restDays.length === 1 ? "is" : "are"} for full rest. Light walking or stretching only.` },
        { title: "Deload Protocol", desc: `Every ${P.deloadEveryNCycles} cycles, volume is reduced by ${Math.round(P.deloadVolumeReduction * 100)}% to allow systemic recovery. Same exercises, lighter weights, fewer sets. This prevents overtraining and primes the body for the next growth phase.` },
        { title: "Check-Ins", desc: "Daily check-ins (sleep, stress, energy, digestion, adherence) feed the coaching system. More data means more precise programming adjustments." },
      ],
    },
    {
      category: "TRACKING",
      items: [
        { title: "Why Track Everything?", desc: "Every data point feeds the intelligence engine. More data means better pattern recognition and smarter coaching decisions. Log every workout, every meal, every check-in." },
        { title: "How to Use Forge", desc: "Today tab: daily dashboard, workouts, meals. Coach tab: AI coaching chat. Program tab: your training split and cycle progress. Profile tab: settings and preferences." },
        { title: "Consistency", desc: "Visible results take 8-12 weeks of consistent effort. Small daily improvements compound into significant transformation. Trust the process." },
      ],
    },
  ];

  return sections;
}
